import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function resolveEatzyMediaUrl(
  url: string | undefined | null
): string | undefined {
  if (!url) return undefined;
  return url;
}

/**
 * Formats an offer expiry date with urgency awareness:
 * - Already expired  → "Expired Jun 15"
 * - Expires today    → "Expires today!"
 * - Within 7 days    → "Expires in N day(s)"
 * - Beyond 7 days    → "Expires Jun 15"
 */
export function formatOfferExpiry(dateStr: string): string {
  const expiry = new Date(dateStr);
  const now = new Date();

  if (expiry < now) {
    return (
      "Expired " +
      expiry.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    );
  }

  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const expiryMidnight = new Date(
    expiry.getFullYear(),
    expiry.getMonth(),
    expiry.getDate()
  );
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntil = Math.round(
    (expiryMidnight.getTime() - todayMidnight.getTime()) / msPerDay
  );

  if (daysUntil === 0) return "Expires today!";
  if (daysUntil <= 7)
    return `Expires in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`;
  return (
    "Expires " +
    expiry.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  );
}

export type OfferExpiryUrgency = "expired" | "critical" | "warning" | "normal";

/**
 * Returns the urgency level of an offer based on remaining days:
 * - "expired"  — already past
 * - "critical" — expires today (0 days)
 * - "warning"  — expires in 1–2 days
 * - "normal"   — expires in 3+ days
 */
export function getOfferExpiryUrgency(dateStr: string): OfferExpiryUrgency {
  const expiry = new Date(dateStr);
  const now = new Date();

  if (expiry < now) return "expired";

  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryMidnight = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntil = Math.round(
    (expiryMidnight.getTime() - todayMidnight.getTime()) / msPerDay
  );

  if (daysUntil === 0) return "critical";
  if (daysUntil <= 2) return "warning";
  return "normal";
}

/**
 * Formats when a pending Kudoz reward becomes available for claiming.
 * Returns null if canClaimAt is null/undefined or already in the past (use that to
 * determine the entry is claimable — render your own "Ready" indicator in that case).
 * Returns a human-readable string like "Available tomorrow at 2:30 PM" if still in the future.
 */
export function formatKudozAvailability(
  canClaimAt: Date | string | null | undefined
): string | null {
  if (!canClaimAt) return null;

  const claimDate =
    canClaimAt instanceof Date ? canClaimAt : new Date(canClaimAt);
  if (isNaN(claimDate.getTime())) return null;

  const now = new Date();

  if (claimDate <= now) {
    return null;
  }

  const timeStr = claimDate.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  const startOfDayAfter = new Date(startOfTomorrow);
  startOfDayAfter.setDate(startOfDayAfter.getDate() + 1);

  const dateStr = claimDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

  if (claimDate < startOfTomorrow) {
    return `Available today, ${dateStr} at ${timeStr}`;
  }

  if (claimDate < startOfDayAfter) {
    return `Available tomorrow, ${dateStr} at ${timeStr}`;
  }

  return `Available ${dateStr} at ${timeStr}`;
}
